import type { EventCategory, EventMetadata } from './types';

export type Tone = 'bad' | 'good' | 'info' | 'neutral' | 'warn';

interface CatalogEntry {
  category: EventCategory;
  label: string;
  summarize: (metadata: EventMetadata) => string;
  tone: Tone | ((metadata: EventMetadata) => Tone);
}

const str = (value: unknown): string => (value == null ? '' : String(value));
const slide = (m: EventMetadata) => {
  if (m.slide_id == null) return 'no slide';
  return m.position == null ? `slide ${str(m.slide_id)}` : `slide ${str(m.position)} (id ${str(m.slide_id)})`;
};
const reason = (m: EventMetadata) => (m.reason ? ` (${str(m.reason)})` : '');
const count = (value: unknown) => (Array.isArray(value) ? value.length : 0);
const none = () => '';

const subscribedSummary = (m: EventMetadata) => {
  const transmitted = m.transmitted_slide as { slide_id?: number } | null;
  const parts = [str(m.role)];
  if (transmitted?.slide_id != null) parts.push(`sent slide ${transmitted.slide_id}`);
  if (m.host_returning_after_drop) parts.push('returning after drop');
  return parts.filter(Boolean).join(' · ');
};

const unsubscribedSummary = (m: EventMetadata) => {
  if (m.role === 'host') return m.last_host_tab ? 'last host tab' : 'another host tab still open';
  return m.presence_removed ? 'presence dropped' : 'was not counted present';
};

const presentSummary = (m: EventMetadata) => {
  if (m.participation_created) return 'first join';
  if (m.newly_added && Number(m.rejoin_count) > 0) return `rejoin #${str(m.rejoin_count)}`;
  return m.newly_added ? '' : 'already present';
};

const suppressedSummary = (m: EventMetadata) => {
  const viewing = m.current_slide_show_id != null ? `, viewing ${str(m.current_slide_show_id)}` : '';
  return `${str(m.reason)} · invited to slideshow ${str(m.notified_slide_show_id)}${viewing}`;
};

// One row per LiveSessionEvent::TYPES entry — eventCatalog.test asserts parity with the server list.
export const EVENT_CATALOG: Record<string, CatalogEntry> = {
  channel_rejected: {
    category: 'connection',
    label: 'Subscription rejected',
    summarize: m => `Reason: ${str(m.reason) || 'unknown'}`,
    tone: 'bad',
  },
  channel_subscribed: {
    category: 'connection',
    label: 'Subscribed',
    summarize: subscribedSummary,
    tone: 'info',
  },
  channel_unsubscribed: {
    category: 'connection',
    label: 'Unsubscribed',
    summarize: unsubscribedSummary,
    tone: 'neutral',
  },
  disconnect_debounce_expired: {
    category: 'connection',
    label: 'Teacher gone > debounce',
    summarize: m => `Unlocked after ${str(m.debounce_ms)}ms without the teacher`,
    tone: 'warn',
  },
  join_notification_suppressed: {
    category: 'entry',
    label: 'Invite ignored',
    summarize: suppressedSummary,
    tone: 'warn',
  },
  join_popup_dismissed: {
    category: 'popup',
    label: 'Join popup dismissed',
    summarize: none,
    tone: 'neutral',
  },
  join_popup_join_clicked: {
    category: 'popup',
    label: 'Join clicked',
    summarize: m => str(m.target_url),
    tone: 'info',
  },
  join_popup_shown: {
    category: 'popup',
    label: 'Join popup shown',
    summarize: m => `${str(m.origin)}${m.dismiss_only ? ' · dismiss only' : ''}`,
    tone: 'info',
  },
  lock_engaged: {
    category: 'lock',
    label: 'Navigation locked',
    summarize: m => str(m.reason),
    tone: 'good',
  },
  lock_released: {
    category: 'lock',
    label: 'Navigation unlocked',
    summarize: m => str(m.reason),
    tone: m => (m.reason === 'session_ended' ? 'neutral' : 'warn'),
  },
  mark_absent: {
    category: 'presence',
    label: 'Marked absent',
    summarize: none,
    tone: 'neutral',
  },
  mark_present: {
    category: 'presence',
    label: 'Marked present',
    summarize: presentSummary,
    tone: 'info',
  },
  nav_applied: {
    category: 'navigation',
    label: 'Slide shown',
    summarize: m => `${slide(m)} · ${str(m.cause)}${m.locked === false ? ' · unlocked' : ''}`,
    tone: m => (m.locked === false ? 'warn' : 'neutral'),
  },
  nav_suppressed: {
    category: 'navigation',
    label: 'Slide ignored',
    summarize: m => `${slide(m)}${reason(m)}`,
    tone: 'warn',
  },
  nav_unresolved: {
    category: 'navigation',
    label: 'Slide not in deck',
    summarize: m => `${slide(m)} · ${m.slides_loaded ? 'deck loaded' : 'deck not loaded yet'}`,
    tone: 'bad',
  },
  navigate_to_slide: {
    category: 'navigation',
    label: 'Teacher navigated',
    summarize: m => (m.rejected_reason ? `${slide(m)} · rejected: ${str(m.rejected_reason)}` : slide(m)),
    tone: m => (m.rejected_reason ? 'bad' : 'neutral'),
  },
  page_entered: {
    category: 'entry',
    label: 'Page loaded',
    summarize: m => `${str(m.entry_mode)} · ${str(m.nav_type)}${m.hydrated_session_id ? '' : ' · not hydrated'}`,
    tone: m => (m.hydrated_session_id ? 'info' : 'warn'),
  },
  page_left: {
    category: 'entry',
    label: 'Page left',
    summarize: m => `${str(m.lock_state)} · socket ${str(m.ws_state)}`,
    tone: 'neutral',
  },
  popup_seeded: {
    category: 'entry',
    label: 'Popup seed',
    summarize: m => `${str(m.result)}${reason(m)}`,
    tone: 'neutral',
  },
  present_mode_hydrated: {
    category: 'entry',
    label: 'Server hydration',
    summarize: m => `${str(m.result)}${reason(m)} · via ${str(m.via) || 'direct'}`,
    tone: m => (m.result === 'free' ? 'warn' : 'info'),
  },
  session_ended: {
    category: 'lifecycle',
    label: 'Session ended',
    summarize: m => str(m.end_reason),
    tone: 'neutral',
  },
  session_ended_received: {
    category: 'lifecycle',
    label: 'Session end received',
    summarize: m => str(m.end_reason),
    tone: 'neutral',
  },
  session_started: {
    category: 'lifecycle',
    label: 'Session started',
    summarize: m => `${str(m.total_student_count)} students in ${count(m.occupied_classroom_ids)} classrooms`,
    tone: 'info',
  },
  silent_reconnect: {
    category: 'connection',
    label: 'Teacher back (silent)',
    summarize: m => `snapped to ${slide(m)}`,
    tone: 'good',
  },
  student_removed: {
    category: 'lifecycle',
    label: 'Student removed',
    summarize: m => `student ${str(m.student_id)}`,
    tone: 'bad',
  },
  student_removed_received: {
    category: 'lifecycle',
    label: 'Removal received',
    summarize: none,
    tone: 'bad',
  },
  students_notified: {
    category: 'lifecycle',
    label: 'Students notified',
    summarize: (m) => {
      const reinvited = count(m.re_invited_ids);
      return `${count(m.student_ids)} invited${reinvited ? `, ${reinvited} re-invited` : ''}`;
    },
    tone: 'info',
  },
  teacher_disconnected: {
    category: 'connection',
    label: 'Teacher dropped',
    summarize: none,
    tone: 'bad',
  },
  teacher_nav_guarded: {
    category: 'navigation',
    label: 'Teacher nav blocked',
    summarize: m => `${slide(m)}${reason(m)}`,
    tone: 'warn',
  },
  teacher_nav_sent: {
    category: 'navigation',
    label: 'Teacher nav sent',
    summarize: m => `${slide(m)} · socket ${str(m.connection_state)}`,
    tone: m => (m.connection_state === 'connected' ? 'neutral' : 'bad'),
  },
  teacher_reconnected: {
    category: 'connection',
    label: 'Teacher back',
    summarize: m => (m.slide_id ? slide(m) : ''),
    tone: 'good',
  },
  ws_connected: {
    category: 'connection',
    label: 'Socket connected',
    summarize: none,
    tone: 'good',
  },
  ws_disconnected: {
    category: 'connection',
    label: 'Socket dropped',
    summarize: m => (m.offline_event ? 'browser went offline' : `cable dropped · online=${str(m.online)}`),
    tone: 'bad',
  },
  ws_rejected: {
    category: 'connection',
    label: 'Socket rejected',
    summarize: none,
    tone: 'bad',
  },
};

const FALLBACK: CatalogEntry = {
  category: 'lifecycle',
  label: '',
  summarize: none,
  tone: 'neutral',
};

export const catalogFor = (eventType: string): CatalogEntry => (
  EVENT_CATALOG[eventType] ?? { ...FALLBACK, label: eventType }
);

export const toneFor = (eventType: string, metadata: EventMetadata): Tone => {
  const { tone } = catalogFor(eventType);
  return typeof tone === 'function' ? tone(metadata) : tone;
};

export const CATEGORIES: { key: EventCategory; label: string }[] = [
  { key: 'lifecycle', label: 'Lifecycle' },
  { key: 'entry', label: 'Entry' },
  { key: 'connection', label: 'Connection' },
  { key: 'lock', label: 'Lock' },
  { key: 'navigation', label: 'Navigation' },
  { key: 'presence', label: 'Presence' },
  { key: 'popup', label: 'Popups' },
];

// Runs of these collapse into one timeline row per actor.
export const NAV_GROUP_TYPES = new Set(['nav_applied', 'navigate_to_slide', 'teacher_nav_sent']);
